import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import Loading from "../../components/loading";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";

import { X } from "lucide-react";
import {
  addUnit,
  deleteUnit,
  getUnits,
  updateUnit,
} from "../../services/services";
import { confirmDelete } from "../../components/confirmDelete";

const Unit = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [nameError, setNameError] = useState(false);
  const nameInputRef = useRef(null);

  const [newUnit, setNewUnit] = useState({
    name: "",
    status: "active",
  });

  const fetchUnits = async () => {
    const payload = { search: "" };
    try {
      setLoading(true);
      const res = await getUnits(payload);
      if (res.data.status) {
        setUnits(res.data.response.units);
      }
    } catch (err) {
      toast.error("Failed to load units");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  const handleAddUnit = async () => {
    if (!newUnit.name.trim()) {
      setNameError(true);
      toast.error("Unit name is required");

      setTimeout(() => {
        nameInputRef.current?.focus();
      }, 0);

      return;
    }

    const payload = {
      name: newUnit.name,
      status: newUnit.status,
    };

    try {
      if (editingId) {
        // Update unit
        const res = await updateUnit(editingId, payload);
        if (res.data.status) {
          toast.success(res.data.message);
          setEditingId(null);
        } else {
          toast.error(res.data.message || "Failed to update unit");
          return;
        }
      } else {
        // Add new unit
        const res = await addUnit(payload);
        if (res.data.status) {
          const addedUnit = res.data.response;
          toast.success(`${addedUnit.name} added!`);
        } else {
          toast.error(res.data.message || "Failed to add unit");
          return;
        }
      }
      closeModal();
      setShowAddModal(false);
      fetchUnits();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save unit");
    }
  };

  const handleUpdate = (unit) => {
    setEditingId(unit._id);
    setNewUnit({ name: unit.name, status: unit.status });
    setShowAddModal(true);
  };

  const handleDelete = async (id, name) => {
    const isConfirm = await confirmDelete(name);
    if (!isConfirm) return;
    try {
      const res = await deleteUnit(id);
      if (res.data.status) {
        toast.success(res.data.message);
        fetchUnits();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete unit");
    }
  };
  const closeModal = () => {
    setShowAddModal(false);
    setEditingId(null);
    setNewUnit({ name: "", status: "active" });
    setNameError(false);
  };

  if (loading && units.length === 0) return <Loading />;

  return (
    <>
      <div id="main">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="custom-title mb-0">Product units</h4>
          <div>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              Add unit
              {/* <i className="bi bi-plus"></i>
              <span className="d-none d-md-inline ms-1"> Add Unit</span> */}
            </button>
          </div>
        </div>

        <div className="card product-list-card shadow-sm">
          <div className="card-body p-0">
            {units.length === 0 ? (
              <div className="text-center p-5">
                <i className="bi bi-rulers display-1 text-muted"></i>
                <p className="py-4 text-gray-500">No units yet.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle table-xxs mb-0">
                  <thead
                    className="table-light small text-muted"
                    style={{ color: "#0b2b6d" }}
                  >
                    <tr>
                      <th>S.No</th>
                      <th>Unit Name</th>
                      <th>Status</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {units.map((unit, i) => (
                      <tr key={unit._id}>
                        <td>{(page - 1) * limit + i + 1}</td>
                        <td className="fw-semibold">{unit.name}</td>
                        <td>
                          {unit.status == "active" ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-danger">In Active</span>
                          )}
                        </td>
                        <td className="text-center">
                          <div>
                            {/* <button
                              className="btn btn-light btn-sm shadow-sm text-primary border"
                              onClick={() => handleUpdate(unit)}
                            >
                              <i class="bi bi-pencil"></i>
                            </button> */}
                            <button
                              className="btn btn-light btn-sm shadow-sm text-danger border"
                              onClick={() => handleDelete(unit._id, unit.name)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showAddModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingId ? "Edit Unit" : "Add Unit"}
                </h5>
                <X onClick={closeModal} style={{ cursor: "pointer" }} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    Unit Name
                    <span className="text-danger ms-1">*</span>
                  </label>
                  <input
                    ref={nameInputRef}
                    type="text"
                    className={`form-control ${nameError ? "is-invalid" : ""}`}
                    value={newUnit.name}
                    onChange={(e) => {
                      setNewUnit({
                        ...newUnit,
                        name: e.target.value,
                      });
                      if (nameError) setNameError(false);
                    }}
                  />
                  {nameError && (
                    <div className="invalid-feedback">
                      Unit name is required
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={newUnit.status}
                    onChange={(e) =>
                      setNewUnit({
                        ...newUnit,
                        status: e.target.value,
                      })
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeModal}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddUnit}
                >
                  {editingId ? "Update Unit" : "Add Unit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default headerLayoutWrapper(Unit);
